<script setup lang="ts">
import { ref, onUnmounted, reactive, watch, onMounted } from 'vue';
import { RouterView } from 'vue-router';
import { watchEffect } from 'vue';

import { useSessionStore } from '@/stores/useSessionStore';
import { useWebSessionStore } from '@/stores/useWebSessionStore';
import { useUserStore } from '@/stores/useUserStore';
import { tabbable } from 'tabbable';

const sessionStore = useSessionStore();
const webSessionStore = useWebSessionStore();
const userStore = useUserStore();
const mainContent = ref<HTMLElement | null>(null);
const navbar = reactive<{
  ref: HTMLElement | null;
  observer: ResizeObserver | null;
  height: number;
  top: number;
  homeWidth: number;
}>({
  ref: null,
  observer: null,
  height: 0,
  top: 0,
  homeWidth: 0,
});

webSessionStore.$subscribe(
  (_, { webSession }) => {
    if (!webSession) {
      webSessionStore.initialize();
      return;
    }

    userStore.setUser(webSession.CurrentUser);
  },
  { immediate: true },
);

watchEffect(() => {
  if (sessionStore.layoutClass || !sessionStore.contentType) return;

  sessionStore.setLayoutClass(sessionStore.contentType);
});

const skipNavigation = () => {
  if (!mainContent.value) return;

  const elements = tabbable(mainContent.value);

  for (const element of elements) {
    // Skip elements that are part of the lesson navigation or the back to overview button
    if (element.closest('.g-lesson-navigation, .g-back-to-overview')) continue;

    element.focus();

    break;
  }
};

const handleTabKey = (event: KeyboardEvent) => {
  if (event.key !== 'Tab') return;

  const target = event.target;

  const elements = tabbable(document.body, { getShadowRoot: true });

  const lastItem = elements.at(event.shiftKey ? 0 : -1);
  const nextItem = elements.at(event.shiftKey ? -1 : 0);

  // If the last item is not focused, do nothing
  if (target !== lastItem && target !== document.body) return;

  event.preventDefault();

  // Focus on the next item to prevent the browser from focusing on the address bar
  nextItem?.focus();
};

watch(
  () => navbar.ref,
  (value) => {
    if (!value) return;

    navbar.observer = new ResizeObserver(() => {
      const nav = value.shadowRoot?.querySelector<HTMLElement>('nav');
      const home = value.shadowRoot?.querySelector<HTMLElement>('.go-home');

      if (!nav || !home) return;

      navbar.top = nav.offsetTop;
      navbar.height = nav.clientHeight;
      navbar.homeWidth = home.clientWidth + home.offsetLeft;
    });

    navbar.observer.observe(value);
  },
);

onMounted(() => {
  document.addEventListener('keydown', handleTabKey);
});

// Clean up the observer on component unmount
onUnmounted(() => {
  navbar.observer?.disconnect();
  document.removeEventListener('keydown', handleTabKey);
});
</script>

<template>
  <div
    class="page-wrapper"
    :style="{
      '--navbar-top': navbar.top + 'px',
      '--navbar-height': navbar.height + 'px',
      '--navbar-home-width': navbar.homeWidth + 'px',
    }"
  >
    <button v-if="sessionStore.isNavigationVisible" class="skip-navigation" @click="skipNavigation">
      {{ $t('actions.skip_navigation') }}
    </button>

    <g-platform-navbar
      v-if="sessionStore.isNavigationVisible"
      :ref="(el: HTMLElement) => (navbar.ref = el)"
      class="g-platform-navbar"
    />

    <main ref="mainContent" :class="['page', sessionStore.layoutClass]">
      <RouterView v-slot="{ Component }" name="dialog">
        <Transition name="dialog" mode="in-out">
          <component :is="Component" />
        </Transition>
      </RouterView>
      <RouterView />
    </main>

    <g-platform-footer v-if="sessionStore.isFooterVisible" class="g-platform-footer" />

    <g-platform-cookie-banner class="g-platform-cookie-banner" />
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/_breakpoints.scss';

.page-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;

  .skip-navigation {
    position: absolute;
    left: -9999px;
    width: 1px;
    height: 1px;
    overflow: hidden;
    z-index: 21;

    &:focus {
      top: calc(var(--navbar-top) + (var(--navbar-height) / 2) - 1rem);
      left: calc(var(--navbar-home-width) + 1rem);
      width: auto;
      height: 2rem;
      padding: 0 0.5rem;
      background-color: var(--white);
      color: var(--color-brand-primary-100);
      font-weight: bold;
      border: 2px solid var(--color-brand-primary-100);
      border-radius: var(--radius-medium);
      outline: none;
      font-size: 0.875rem;
    }
  }

  .g-platform-navbar {
    position: relative;
  }

  .g-platform-footer {
    z-index: 18;
  }

  .g-platform-navbar {
    z-index: 19;
  }

  .g-platform-cookie-banner {
    z-index: 20;
  }

  .page {
    flex-grow: 1;

    &.courses {
      display: flex;
      flex-direction: column;
      background-color: var(--grey-10);
      min-height: 100vh;

      .dialog-enter-active,
      .dialog-leave-active {
        transition: all 0.4s ease;
      }

      .dialog-enter-from,
      .dialog-leave-to {
        opacity: 0;
        transform: scale(0.5);
      }

      @media (prefers-reduced-motion) {
        .dialog-enter-active,
        .dialog-leave-active {
          transition: none;
        }
      }
    }
  }
}
</style>
